import React, { useState, useEffect } from 'react';
import { useEntitlements } from './EntitlementsContext';
import { useJWTAuth } from './auth/JWTAuthContext';
import AdminLedger from './pages/AdminLedger';
import AdminTokens from './pages/AdminTokens';
import AdminBackfill from './pages/AdminBackfill';
import AdminBattlefieldTokens from './pages/AdminBattlefieldTokens';

const Admin = () => {
  const { userId, isAdmin } = useEntitlements();
  const { getAuthHeaders } = useJWTAuth();
  const [activeTab, setActiveTab] = useState('grant');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Grant Entitlement Form State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableEntitlements, setAvailableEntitlements] = useState([]);
  const [selectedEntitlement, setSelectedEntitlement] = useState('');
  const [entitlementType, setEntitlementType] = useState('admin');
  const [expiresAt, setExpiresAt] = useState('');
  const [notes, setNotes] = useState('');
  
  // Wallet visibility state
  const [revealedWallets, setRevealedWallets] = useState(new Set());

  // View Entitlements State
  const [viewUserId, setViewUserId] = useState('');
  const [userEntitlements, setUserEntitlements] = useState([]);

  // Manage Entitlements State
  const [newEntitlementId, setNewEntitlementId] = useState('');
  const [newEntitlementName, setNewEntitlementName] = useState('');
  const [newEntitlementDescription, setNewEntitlementDescription] = useState('');
  const [newEntitlementCategory, setNewEntitlementCategory] = useState('game_feature');
  const [availableThemes, setAvailableThemes] = useState([]);
  const [selectedThemeId, setSelectedThemeId] = useState('');

  // Armies Management State
  const [armies, setArmies] = useState([]);
  const [editingArmy, setEditingArmy] = useState(null);
  const [armyForm, setArmyForm] = useState({
    id: '',
    name: '',
    chain: 'solana',
    army_type: 'token',
    contract_address: '',
    default_theme_id: 'soulwars-default',
    default_player_count: 100,
    is_default: false,
    is_active: true,
    nft_api_endpoint: ''
  });

  // Heroes Management State
  const [heroSearch, setHeroSearch] = useState('');
  const [heroResults, setHeroResults] = useState([]);
  const [recentHeroes, setRecentHeroes] = useState([]);
  const [selectedHero, setSelectedHero] = useState(null);
  const [xpAmount, setXpAmount] = useState('');
  const [xpReason, setXpReason] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);

  // Password Management State
  const [passwordSearchQuery, setPasswordSearchQuery] = useState('');
  const [passwordSearchResults, setPasswordSearchResults] = useState([]);
  const [selectedPasswordUser, setSelectedPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Fetch available entitlements and themes on mount
  useEffect(() => {
    fetchAvailableEntitlements();
    fetchAvailableThemes();
    if (activeTab === 'armies') {
      fetchArmies();
    }
    if (activeTab === 'heroes') {
      fetchRecentHeroes();
    }
  }, [activeTab]);

  const fetchAvailableThemes = async () => {
    try {
      const response = await fetch('/api/themes', {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'soulwarz'
        }
      });
      if (response.ok) {
        const themes = await response.json();
        setAvailableThemes(themes || []);
        console.log('📋 [Admin] Loaded available themes:', themes);
      }
    } catch (error) {
      console.error('Error fetching themes:', error);
    }
  };

  // Armies Management Functions
  const fetchArmies = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/armies', {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setArmies(data.data || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch armies' });
      }
    } catch (error) {
      console.error('Error fetching armies:', error);
      setMessage({ type: 'error', text: 'Error fetching armies' });
    } finally {
      setLoading(false);
    }
  };

  const handleArmyFormChange = (field, value) => {
    setArmyForm(prev => ({ ...prev, [field]: value }));
  };

  const resetArmyForm = () => {
    setArmyForm({
      id: '',
      name: '',
      chain: 'solana',
      army_type: 'token',
      contract_address: '',
      default_theme_id: 'soulwars-default',
      default_player_count: 100,
      is_default: false,
      is_active: true,
      nft_api_endpoint: ''
    });
    setEditingArmy(null);
  };

  const createArmy = async (e) => {
    e.preventDefault();

    if (!armyForm.id || !armyForm.name || !armyForm.contract_address) {
      setMessage({ type: 'error', text: 'Please fill in required fields: ID, Name, and Contract Address' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/armies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          ...armyForm,
          userId
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Army created successfully!' });
        resetArmyForm();
        fetchArmies();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to create army' });
      }
    } catch (error) {
      console.error('Error creating army:', error);
      setMessage({ type: 'error', text: 'Error creating army' });
    } finally {
      setLoading(false);
    }
  };

  const updateArmy = async (e) => {
    e.preventDefault();

    if (!editingArmy) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/armies/${editingArmy}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({
          ...armyForm,
          userId
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Army updated successfully!' });
        resetArmyForm();
        fetchArmies();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to update army' });
      }
    } catch (error) {
      console.error('Error updating army:', error);
      setMessage({ type: 'error', text: 'Error updating army' });
    } finally {
      setLoading(false);
    }
  };

  const editArmy = (army) => {
    setArmyForm({
      id: army.id,
      name: army.name,
      chain: army.chain,
      army_type: army.army_type,
      contract_address: army.contract_address,
      default_theme_id: army.default_theme_id || 'soulwars-default',
      default_player_count: army.default_player_count,
      is_default: army.is_default,
      is_active: army.is_active,
      nft_api_endpoint: army.nft_api_endpoint || ''
    });
    setEditingArmy(army.id);
  };

  const deleteArmy = async (armyId) => {
    if (!window.confirm('Are you sure you want to delete this army? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/armies/${armyId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Army deleted successfully!' });
        fetchArmies();
      } else {
        setMessage({ type: 'error', text: 'Failed to delete army' });
      }
    } catch (error) {
      console.error('Error deleting army:', error);
      setMessage({ type: 'error', text: 'Error deleting army' });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to truncate wallet address
  const truncateWallet = (address) => {
    if (!address || address.length < 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Toggle wallet visibility
  const toggleWalletVisibility = (walletAddress) => {
    setRevealedWallets(prev => {
      const newSet = new Set(prev);
      if (newSet.has(walletAddress)) {
        newSet.delete(walletAddress);
      } else {
        newSet.add(walletAddress);
      }
      return newSet;
    });
  };

  // Render wallet with toggle button
  const WalletDisplay = ({ address, className = '' }) => {
    const isRevealed = revealedWallets.has(address);
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="font-mono text-sm">
          {isRevealed ? address : truncateWallet(address)}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleWalletVisibility(address);
          }}
          className="text-gray-400 hover:text-white transition-colors"
          title={isRevealed ? 'Hide wallet address' : 'Show full wallet address'}
        >
          {isRevealed ? (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  const fetchAvailableEntitlements = async () => {
    try {
      const response = await fetch('/api/entitlements');
      if (response.ok) {
        const data = await response.json();
        setAvailableEntitlements(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching entitlements:', error);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    if (!userId) {
      setMessage({ type: 'error', text: 'You must be logged in to search users' });
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 [Admin] Searching users with query:', searchQuery, 'userId:', userId);
      const response = await fetch(`/api/entitlements/search-users?query=${encodeURIComponent(searchQuery)}&userId=${userId}`, {
        headers: getAuthHeaders()
      });
      
      console.log('🔍 [Admin] Search response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ [Admin] Search results:', data);
        setSearchResults(data.data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ [Admin] Search failed:', errorData);
        setMessage({ type: 'error', text: errorData.error || 'Failed to search users' });
      }
    } catch (error) {
      console.error('❌ [Admin] Error searching users:', error);
      setMessage({ type: 'error', text: 'Error searching users' });
    } finally {
      setLoading(false);
    }
  };

  const grantEntitlement = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedEntitlement) {
      setMessage({ type: 'error', text: 'Please select a user and entitlement' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/entitlements/grant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          userId,
          targetUserId: selectedUser.id,
          entitlementId: selectedEntitlement,
          entitlementType,
          expiresAt: expiresAt || null,
          notes: notes || null
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entitlement granted successfully!' });
        // Reset form
        setSelectedUser(null);
        setSelectedEntitlement('');
        setExpiresAt('');
        setNotes('');
        setSearchQuery('');
        setSearchResults([]);
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to grant entitlement' });
      }
    } catch (error) {
      console.error('Error granting entitlement:', error);
      setMessage({ type: 'error', text: 'Error granting entitlement' });
    } finally {
      setLoading(false);
    }
  };

  const viewUserEntitlements = async () => {
    if (!viewUserId) {
      setMessage({ type: 'error', text: 'Please enter a user ID' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/entitlements/user/${viewUserId}`);
      
      if (response.ok) {
        const data = await response.json();
        setUserEntitlements(data.data || []);
        if (data.data.length === 0) {
          setMessage({ type: 'info', text: 'No entitlements found for this user' });
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch user entitlements' });
      }
    } catch (error) {
      console.error('Error fetching user entitlements:', error);
      setMessage({ type: 'error', text: 'Error fetching user entitlements' });
    } finally {
      setLoading(false);
    }
  };

  const revokeEntitlement = async (userEntitlementId) => {
    if (!window.confirm('Are you sure you want to revoke this entitlement?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/entitlements/revoke/${userEntitlementId}?userId=${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entitlement revoked successfully' });
        // Refresh the list
        viewUserEntitlements();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to revoke entitlement' });
      }
    } catch (error) {
      console.error('Error revoking entitlement:', error);
      setMessage({ type: 'error', text: 'Error revoking entitlement' });
    } finally {
      setLoading(false);
    }
  };

  const createEntitlement = async (e) => {
    e.preventDefault();

    // For theme category, validate theme selection
    if (newEntitlementCategory === 'theme' && !selectedThemeId) {
      setMessage({ type: 'error', text: 'Please select a theme' });
      return;
    }

    // Auto-format entitlement ID for themes
    let finalEntitlementId = newEntitlementId;
    if (newEntitlementCategory === 'theme' && selectedThemeId) {
      finalEntitlementId = `theme:${selectedThemeId}`;
    }

    if (!finalEntitlementId || !newEntitlementName || !newEntitlementCategory) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setLoading(true);
      console.log('📝 [Admin] Creating entitlement:', { id: finalEntitlementId, name: newEntitlementName, category: newEntitlementCategory });
      
      const response = await fetch('/api/entitlements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          userId,
          id: finalEntitlementId,
          name: newEntitlementName,
          description: newEntitlementDescription,
          entitlement_category: newEntitlementCategory
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Entitlement created successfully!' });
        // Reset form
        setNewEntitlementId('');
        setNewEntitlementName('');
        setNewEntitlementDescription('');
        setNewEntitlementCategory('game_feature');
        setSelectedThemeId('');
        // Refresh available entitlements
        fetchAvailableEntitlements();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to create entitlement' });
      }
    } catch (error) {
      console.error('Error creating entitlement:', error);
      setMessage({ type: 'error', text: 'Error creating entitlement' });
    } finally {
      setLoading(false);
    }
  };

  // Heroes Management Functions
  const fetchRecentHeroes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/leaderboards/recent?limit=50');
      const data = await response.json();
      
      if (data.success) {
        setRecentHeroes(data.data.heroes || []);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch recent heroes' });
      }
    } catch (error) {
      console.error('Error fetching recent heroes:', error);
      setMessage({ type: 'error', text: 'Error fetching recent heroes' });
    } finally {
      setLoading(false);
    }
  };

  const searchHeroes = async () => {
    if (!heroSearch.trim()) {
      setHeroResults([]);
      setShowSearchResults(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/leaderboards/search?q=${encodeURIComponent(heroSearch)}`);
      const data = await response.json();
      
      if (data.success) {
        setHeroResults(data.data.heroes || []);
        setShowSearchResults(true);
      } else {
        setMessage({ type: 'error', text: 'Failed to search heroes' });
      }
    } catch (error) {
      console.error('Error searching heroes:', error);
      setMessage({ type: 'error', text: 'Error searching heroes' });
    } finally {
      setLoading(false);
    }
  };

  const awardXP = async () => {
    if (!selectedHero || !xpAmount || xpAmount <= 0) {
      setMessage({ type: 'error', text: 'Please select a hero and enter a valid XP amount' });
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/heroes/${selectedHero.id}/admin-award-xp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          xpAmount: parseInt(xpAmount),
          reason: xpReason || 'Admin award'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: `Successfully awarded ${xpAmount} XP to ${selectedHero.name}` 
        });
        
        // Update the selected hero with new data
        setSelectedHero(data.data.hero);
        
        // Clear the form
        setXpAmount('');
        setXpReason('');
        
        // Refresh both search results and recent heroes to show updated XP
        if (heroSearch.trim()) {
          searchHeroes();
        }
        fetchRecentHeroes();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to award XP' });
      }
    } catch (error) {
      console.error('Error awarding XP:', error);
      setMessage({ type: 'error', text: 'Error awarding XP' });
    } finally {
      setLoading(false);
    }
  };

  // Password Management Functions
  const searchUsersForPassword = async () => {
    if (!passwordSearchQuery.trim()) {
      setPasswordSearchResults([]);
      return;
    }

    if (!userId) {
      setMessage({ type: 'error', text: 'You must be logged in to search users' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/entitlements/search-users?query=${encodeURIComponent(passwordSearchQuery)}&userId=${userId}`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setPasswordSearchResults(data.data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setMessage({ type: 'error', text: errorData.error || 'Failed to search users' });
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setMessage({ type: 'error', text: 'Error searching users' });
    } finally {
      setLoading(false);
    }
  };

  const setUserPassword = async (e) => {
    e.preventDefault();
    
    if (!selectedPasswordUser) {
      setMessage({ type: 'error', text: 'Please select a user' });
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/auth/set-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          userId: selectedPasswordUser.id,
          password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ 
          type: 'success', 
          text: `Password set successfully for ${selectedPasswordUser.username || 'user'}` 
        });
        // Reset form
        setSelectedPasswordUser(null);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordSearchQuery('');
        setPasswordSearchResults([]);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to set password' });
      }
    } catch (error) {
      console.error('Error setting password:', error);
      setMessage({ type: 'error', text: 'Error setting password' });
    } finally {
      setLoading(false);
    }
  };

  // Check if user has admin access
  if (!userId) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Admin Access Required</h1>
          <p className="text-gray-400">Please connect your wallet to access the admin panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-8">Admin Panel</h1>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-600' :
            message.type === 'error' ? 'bg-red-600' :
            'bg-blue-600'
          }`}>
            {message.text}
            <button 
              onClick={() => setMessage(null)}
              className="float-right font-bold"
            >×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-gray-700">
          <button
            onClick={() => setActiveTab('grant')}
            className={`pb-2 px-4 ${activeTab === 'grant' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Grant Entitlement
          </button>
          <button
            onClick={() => setActiveTab('view')}
            className={`pb-2 px-4 ${activeTab === 'view' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            View User Entitlements
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`pb-2 px-4 ${activeTab === 'manage' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Manage Entitlements
          </button>
          <button
            onClick={() => setActiveTab('armies')}
            className={`pb-2 px-4 ${activeTab === 'armies' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Armies
          </button>
          <button
            onClick={() => setActiveTab('heroes')}
            className={`pb-2 px-4 ${activeTab === 'heroes' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Heroes
          </button>
          <button
            onClick={() => setActiveTab('ledger')}
            className={`pb-2 px-4 ${activeTab === 'ledger' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Ledger
          </button>
          <button
            onClick={() => setActiveTab('tokens')}
            className={`pb-2 px-4 ${activeTab === 'tokens' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Tokens
          </button>
          <button
            onClick={() => setActiveTab('backfill')}
            className={`pb-2 px-4 ${activeTab === 'backfill' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Backfill
          </button>
          <button
            onClick={() => setActiveTab('passwords')}
            className={`pb-2 px-4 ${activeTab === 'passwords' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            User Passwords
          </button>
          <button
            onClick={() => setActiveTab('battlefield-tokens')}
            className={`pb-2 px-4 ${activeTab === 'battlefield-tokens' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-400'}`}
          >
            Battlefield Tokens
          </button>
        </div>

        {/* Grant Entitlement Tab */}
        {activeTab === 'grant' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">Grant Entitlement to User</h2>
            
            {/* User Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Search User</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                  placeholder="Enter wallet address, username, or email"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2"
                />
                <button
                  onClick={searchUsers}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded disabled:opacity-50"
                >
                  Search
                </button>
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-4 bg-gray-700 rounded-lg max-h-60 overflow-y-auto">
                  {searchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setSearchResults([]);
                      }}
                      className="p-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600"
                    >
                      <div className="font-medium">{user.username || 'No username'}</div>
                      <WalletDisplay address={user.solana_wallet_address} className="text-gray-400" />
                      <div className="text-xs text-gray-500">ID: {user.id}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected User */}
              {selectedUser && (
                <div className="mt-4 bg-green-900 bg-opacity-30 border border-green-600 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium mb-1">{selectedUser.username || 'No username'}</div>
                      <WalletDisplay address={selectedUser.solana_wallet_address} className="text-gray-300 mb-1" />
                      <div className="text-xs text-gray-400">User ID: {selectedUser.id}</div>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="text-red-400 hover:text-red-300 ml-4"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Entitlement Form */}
            <form onSubmit={grantEntitlement}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Entitlement</label>
                <select
                  value={selectedEntitlement}
                  onChange={(e) => setSelectedEntitlement(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                  required
                >
                  <option value="">Select an entitlement</option>
                  {availableEntitlements.map(ent => (
                    <option key={ent.id} value={ent.id}>
                      {ent.name} ({ent.entitlement_category})
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Grant Type</label>
                <select
                  value={entitlementType}
                  onChange={(e) => setEntitlementType(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                >
                  <option value="admin">Admin Grant</option>
                  <option value="purchased">Purchased</option>
                  <option value="trial">Trial</option>
                  <option value="gift">Gift</option>
                  <option value="creator">Creator</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Expires At (optional)</label>
                <input
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                />
                <p className="text-xs text-gray-400 mt-1">Leave empty for permanent access</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                  rows="3"
                  placeholder="Add any notes about this grant..."
                />
              </div>

              <button
                type="submit"
                disabled={loading || !selectedUser || !selectedEntitlement}
                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Granting...' : 'Grant Entitlement'}
              </button>
            </form>
          </div>
        )}

        {/* View User Entitlements Tab */}
        {activeTab === 'view' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">View User Entitlements</h2>
            
            <div className="flex gap-2 mb-6">
              <input
                type="number"
                value={viewUserId}
                onChange={(e) => setViewUserId(e.target.value)}
                placeholder="Enter User ID"
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2"
              />
              <button
                onClick={viewUserEntitlements}
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded disabled:opacity-50"
              >
                View
              </button>
            </div>

            {userEntitlements.length > 0 && (
              <div className="space-y-4">
                {userEntitlements.map(ent => (
                  <div key={ent.user_entitlement_id} className="bg-gray-700 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{ent.name}</h3>
                        <p className="text-sm text-gray-400 mb-2">{ent.description}</p>
                        <div className="text-xs space-y-1">
                          <div><span className="text-gray-500">ID:</span> {ent.entitlement_id}</div>
                          <div><span className="text-gray-500">Type:</span> {ent.entitlement_type}</div>
                          <div><span className="text-gray-500">Category:</span> {ent.entitlement_category}</div>
                          <div><span className="text-gray-500">Granted:</span> {new Date(ent.granted_at).toLocaleString()}</div>
                          {ent.expires_at && (
                            <div><span className="text-gray-500">Expires:</span> {new Date(ent.expires_at).toLocaleString()}</div>
                          )}
                          {ent.notes && (
                            <div><span className="text-gray-500">Notes:</span> {ent.notes}</div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => revokeEntitlement(ent.user_entitlement_id)}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm disabled:opacity-50"
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Manage Entitlements Tab */}
        {activeTab === 'manage' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">Create New Entitlement</h2>
            
            <form onSubmit={createEntitlement} className="mb-8">
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Category *</label>
                <select
                  value={newEntitlementCategory}
                  onChange={(e) => {
                    setNewEntitlementCategory(e.target.value);
                    // Clear related fields when switching categories
                    setNewEntitlementId('');
                    setSelectedThemeId('');
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                  required
                >
                  <option value="game_feature">Game Feature</option>
                  <option value="account_feature">Account Feature</option>
                  <option value="theme">Theme</option>
                  <option value="tool">Tool</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {newEntitlementCategory === 'theme' 
                    ? 'Select a theme to create an entitlement for' 
                    : newEntitlementCategory === 'game_feature'
                    ? 'For features like game_access, tournament_access, etc.'
                    : newEntitlementCategory === 'account_feature'
                    ? 'For account features like hero-limit-2, hero-limit-5, etc.'
                    : 'For tools like theme_creator, map_editor, etc.'}
                </p>
              </div>

              {/* Theme Selector - Only show for theme category */}
              {newEntitlementCategory === 'theme' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Select Theme *</label>
                  <select
                    value={selectedThemeId}
                    onChange={(e) => {
                      setSelectedThemeId(e.target.value);
                      // Auto-populate name and description from theme
                      const theme = availableThemes.find(t => t.id === e.target.value);
                      if (theme) {
                        setNewEntitlementName(`${theme.name} Theme Access`);
                        setNewEntitlementDescription(`Access to the ${theme.name} custom theme`);
                      }
                    }}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                    required
                  >
                    <option value="">Choose a theme...</option>
                    {availableThemes.map(theme => (
                      <option key={theme.id} value={theme.id}>
                        {theme.name} (ID: {theme.id})
                      </option>
                    ))}
                  </select>
                  {selectedThemeId && (
                    <p className="text-xs text-green-400 mt-1">
                      Will create entitlement ID: theme:{selectedThemeId}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Entitlement ID *</label>
                  <input
                    type="text"
                    value={newEntitlementId}
                    onChange={(e) => setNewEntitlementId(e.target.value)}
                    placeholder={newEntitlementCategory === 'game_feature' ? 'e.g., game_access, tournament_access' : 'e.g., theme_creator, map_editor'}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Use lowercase with underscores (e.g., game_access)
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input
                  type="text"
                  value={newEntitlementName}
                  onChange={(e) => setNewEntitlementName(e.target.value)}
                  placeholder="Display name"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newEntitlementDescription}
                  onChange={(e) => setNewEntitlementDescription(e.target.value)}
                  placeholder="What does this entitlement provide?"
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2"
                  rows="3"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded font-semibold disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create Entitlement'}
              </button>
            </form>

            {/* List Existing Entitlements */}
            <div>
              <h3 className="text-xl font-bold mb-4">Existing Entitlements</h3>
              <div className="space-y-3">
                {availableEntitlements.map(ent => (
                  <div key={ent.id} className="bg-gray-700 rounded-lg p-4">
                    <div className="font-bold">{ent.name}</div>
                    <div className="text-sm text-gray-400">{ent.description}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      <span className="mr-4">ID: {ent.id}</span>
                      <span>Category: {ent.entitlement_category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Armies Tab */}
        {activeTab === 'armies' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">⚔️ Army Management</h2>

            {/* Army Form (Create/Edit) */}
            <form onSubmit={editingArmy ? updateArmy : createArmy} className="mb-8 bg-gray-700 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-4">{editingArmy ? 'Edit Army' : 'Create New Army'}</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Army ID *</label>
                  <input
                    type="text"
                    value={armyForm.id}
                    onChange={(e) => handleArmyFormChange('id', e.target.value)}
                    disabled={!!editingArmy}
                    placeholder="e.g., soulwars-token-army"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white disabled:opacity-50"
                    required
                  />
                  <p className="text-xs text-gray-400 mt-1">Unique identifier (cannot be changed after creation)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Display Name *</label>
                  <input
                    type="text"
                    value={armyForm.name}
                    onChange={(e) => handleArmyFormChange('name', e.target.value)}
                    placeholder="e.g., SoulWars Token Holders"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Blockchain Chain</label>
                  <select
                    value={armyForm.chain}
                    onChange={(e) => handleArmyFormChange('chain', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  >
                    <option value="solana">Solana</option>
                    <option value="sui">Sui</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Army Type</label>
                  <select
                    value={armyForm.army_type}
                    onChange={(e) => handleArmyFormChange('army_type', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  >
                    <option value="token">Token Holders</option>
                    <option value="nft">NFT Collection</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Contract Address *</label>
                <input
                  type="text"
                  value={armyForm.contract_address}
                  onChange={(e) => handleArmyFormChange('contract_address', e.target.value)}
                  placeholder="Token mint address or NFT collection address"
                  className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white font-mono text-sm"
                  required
                />
              </div>

              {armyForm.army_type === 'nft' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">NFT API Endpoint</label>
                  <input
                    type="url"
                    value={armyForm.nft_api_endpoint}
                    onChange={(e) => handleArmyFormChange('nft_api_endpoint', e.target.value)}
                    placeholder="https://api.example.com/nfts"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white font-mono text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">External API for fetching NFT data</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Default Theme</label>
                  <select
                    value={armyForm.default_theme_id || 'soulwars-default'}
                    onChange={(e) => handleArmyFormChange('default_theme_id', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  >
                    <option value="soulwars-default">SoulWars (Default)</option>
                    {availableThemes
                      .filter(t => t.id !== 'soulwars-default')
                      .map(theme => (
                        <option key={theme.id} value={theme.id}>
                          {theme.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Default Player Count</label>
                  <input
                    type="number"
                    value={armyForm.default_player_count}
                    onChange={(e) => handleArmyFormChange('default_player_count', parseInt(e.target.value))}
                    min="10"
                    max="200"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6 mb-6">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={armyForm.is_default}
                    onChange={(e) => handleArmyFormChange('is_default', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Set as default army (auto-loads on startup)</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={armyForm.is_active}
                    onChange={(e) => handleArmyFormChange('is_active', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">Active (available for selection)</span>
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (editingArmy ? 'Updating...' : 'Creating...') : (editingArmy ? 'Update Army' : 'Create Army')}
                </button>
                
                {editingArmy && (
                  <button
                    type="button"
                    onClick={resetArmyForm}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {/* List of Armies */}
            <div>
              <h3 className="text-xl font-bold mb-4">Existing Armies</h3>
              {loading && armies.length === 0 ? (
                <div className="text-center text-gray-400 py-8">Loading armies...</div>
              ) : armies.length === 0 ? (
                <div className="text-center text-gray-400 py-8">No armies configured yet</div>
              ) : (
                <div className="space-y-3">
                  {armies.map(army => (
                    <div key={army.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-bold text-lg">{army.name}</span>
                            <span className="text-xs px-2 py-1 rounded bg-purple-600">
                              {army.chain === 'solana' ? 'SOL' : 'SUI'}
                            </span>
                            <span className="text-xs px-2 py-1 rounded bg-blue-600">
                              {army.army_type === 'token' ? 'Token' : 'NFT'}
                            </span>
                            {army.is_default && (
                              <span className="text-xs px-2 py-1 rounded bg-green-600">DEFAULT</span>
                            )}
                            {!army.is_active && (
                              <span className="text-xs px-2 py-1 rounded bg-red-600">INACTIVE</span>
                            )}
                          </div>
                          
                          <div className="text-sm text-gray-300 space-y-1">
                            <div className="font-mono text-xs text-gray-400">ID: {army.id}</div>
                            <div className="font-mono text-xs break-all">Contract: {army.contract_address}</div>
                            <div className="text-xs">
                              <span className="text-gray-400">Theme:</span> {availableThemes.find(t => t.id === army.default_theme_id)?.name || 'Default'}
                              <span className="mx-2">•</span>
                              <span className="text-gray-400">Default Players:</span> {army.default_player_count}
                            </div>
                            {army.nft_api_endpoint && (
                              <div className="text-xs">
                                <span className="text-gray-400">API:</span> {army.nft_api_endpoint}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex space-x-2 ml-4">
                          <button
                            onClick={() => editArmy(army)}
                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => deleteArmy(army.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Heroes Tab */}
        {activeTab === 'heroes' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">Heroes Management</h2>
            
            {/* Hero Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Search Heroes</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={heroSearch}
                  onChange={(e) => {
                    setHeroSearch(e.target.value);
                    if (!e.target.value.trim()) {
                      setShowSearchResults(false);
                    }
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && searchHeroes()}
                  placeholder="Enter hero name, username, or chat name"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2"
                />
                <button
                  onClick={searchHeroes}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  Search
                </button>
                {showSearchResults && (
                  <button
                    onClick={() => {
                      setShowSearchResults(false);
                      setHeroSearch('');
                      setHeroResults([]);
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                  >
                    Show Recent
                  </button>
                )}
              </div>
            </div>

            {/* Search Results */}
            {showSearchResults && heroResults.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">Search Results ({heroResults.length})</h3>
                <div className="space-y-2">
                  {heroResults.map(hero => (
                    <div
                      key={hero.id}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedHero?.id === hero.id 
                          ? 'bg-blue-600 border-2 border-blue-400' 
                          : 'bg-gray-700 hover:bg-gray-600'
                      }`}
                      onClick={() => setSelectedHero(hero)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-white font-medium">{hero.name}</div>
                          <div className="text-gray-400 text-sm">by {hero.owner_username}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-yellow-400 font-bold">Level {hero.level}</div>
                          <div className="text-gray-400 text-sm">{(hero.total_xp || 0).toLocaleString()} XP</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Heroes (Default View) */}
            {!showSearchResults && (
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">Recent Heroes ({recentHeroes.length})</h3>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Loading recent heroes...</div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {recentHeroes.map(hero => (
                      <div
                        key={hero.id}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedHero?.id === hero.id 
                            ? 'bg-blue-600 border-2 border-blue-400' 
                            : 'bg-gray-700 hover:bg-gray-600'
                        }`}
                        onClick={() => setSelectedHero(hero)}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="text-white font-medium">{hero.name}</div>
                            <div className="text-gray-400 text-sm">by {hero.owner_username}</div>
                            <div className="text-gray-500 text-xs">
                              Created: {new Date(hero.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-yellow-400 font-bold">Level {hero.level}</div>
                            <div className="text-gray-400 text-sm">{(hero.total_xp || 0).toLocaleString()} XP</div>
                            <div className="text-green-400 text-xs">{(hero.unspent_xp || 0).toLocaleString()} unspent</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Hero Details */}
            {selectedHero && (
              <div className="mb-6 p-4 bg-gray-700 rounded-lg">
                <h3 className="text-lg font-bold mb-3">Selected Hero: {selectedHero.name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Owner:</span>
                    <div className="text-white">{selectedHero.owner_username}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Level:</span>
                    <div className="text-yellow-400 font-bold">{selectedHero.level}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Total XP:</span>
                    <div className="text-white">{(selectedHero.total_xp || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-400">Unspent XP:</span>
                    <div className="text-green-400">{(selectedHero.unspent_xp || 0).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}

            {/* XP Award Form */}
            {selectedHero && (
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-lg font-bold mb-4">Award XP</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">XP Amount</label>
                    <input
                      type="number"
                      value={xpAmount}
                      onChange={(e) => setXpAmount(e.target.value)}
                      placeholder="Enter XP amount to award"
                      className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                    <input
                      type="text"
                      value={xpReason}
                      onChange={(e) => setXpReason(e.target.value)}
                      placeholder="Reason for awarding XP"
                      className="w-full bg-gray-600 border border-gray-500 rounded px-3 py-2"
                    />
                  </div>
                  <button
                    onClick={awardXP}
                    disabled={loading || !xpAmount || xpAmount <= 0}
                    className={`px-6 py-2 rounded font-medium transition-colors ${
                      loading || !xpAmount || xpAmount <= 0
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {loading ? 'Awarding...' : 'Award XP'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'ledger' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <AdminLedger />
          </div>
        )}

        {activeTab === 'tokens' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <AdminTokens />
          </div>
        )}

        {activeTab === 'backfill' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <AdminBackfill />
          </div>
        )}

        {/* User Passwords Tab */}
        {activeTab === 'passwords' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-6">🔐 User Password Management</h2>
            <p className="text-gray-400 mb-6">
              Set or reset passwords for users to enable OBS/streaming login without wallet connection.
            </p>
            
            {/* User Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">Search User</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={passwordSearchQuery}
                  onChange={(e) => setPasswordSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchUsersForPassword()}
                  placeholder="Enter wallet address, username, or email"
                  className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2"
                />
                <button
                  onClick={searchUsersForPassword}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded disabled:opacity-50"
                >
                  Search
                </button>
              </div>

              {/* Search Results */}
              {passwordSearchResults.length > 0 && (
                <div className="mt-4 bg-gray-700 rounded-lg max-h-60 overflow-y-auto">
                  {passwordSearchResults.map(user => (
                    <div
                      key={user.id}
                      onClick={() => {
                        setSelectedPasswordUser(user);
                        setPasswordSearchResults([]);
                        setPasswordSearchQuery('');
                      }}
                      className="p-3 hover:bg-gray-600 cursor-pointer border-b border-gray-600"
                    >
                      <div className="font-medium">{user.username || 'No username'}</div>
                      <WalletDisplay address={user.solana_wallet_address} className="text-gray-400" />
                      <div className="text-xs text-gray-500">ID: {user.id}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected User */}
              {selectedPasswordUser && (
                <div className="mt-4 bg-green-900 bg-opacity-30 border border-green-600 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium mb-1">{selectedPasswordUser.username || 'No username'}</div>
                      <WalletDisplay address={selectedPasswordUser.solana_wallet_address} className="text-gray-300 mb-1" />
                      <div className="text-xs text-gray-400">User ID: {selectedPasswordUser.id}</div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedPasswordUser(null);
                        setNewPassword('');
                        setConfirmPassword('');
                      }}
                      className="text-red-400 hover:text-red-300 ml-4"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Password Form */}
            {selectedPasswordUser && (
              <form onSubmit={setUserPassword} className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">Set Password for {selectedPasswordUser.username || 'User'}</h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-4 py-2"
                    required
                    minLength={6}
                  />
                  <p className="text-xs text-gray-400 mt-1">Password must be at least 6 characters long</p>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter password"
                    className="w-full bg-gray-600 border border-gray-500 rounded px-4 py-2"
                    required
                    minLength={6}
                  />
                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !newPassword || newPassword.length < 6 || newPassword !== confirmPassword}
                  className="w-full bg-green-600 hover:bg-green-700 py-3 rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Setting Password...' : 'Set Password'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Battlefield Tokens Tab */}
        {activeTab === 'battlefield-tokens' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <AdminBattlefieldTokens />
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;

